import { Chip, TextField as MuiTextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useMemo } from "react";
import { useTranslate } from "react-admin";
import { roundToTwo, sumNumericBy } from "utils/math";

const CORE_MAPPING_FIELDS = [
	{ source: "ext_id", label: "tab.pallets.external_id" },
	{ source: "quantity", label: "tab.pallets.quantity" },
	{ source: "net_weight", label: "tab.pallets.weight_net" },
	{ source: "unit_weights", label: "osfc.dialogs.generate-pallets-from-pdf.unit_weights" },
];

// `rowSource` names the field on a preview row, which is not always the mapping key.
const OPTIONAL_MAPPING_FIELDS = [
	{ source: "lot_number", label: "tab.pallets.lot_number" },
	{ source: "packing_station", label: "tab.pallets.packing_station" },
	{
		source: "production_date",
		rowSource: "production_datetime",
		label: "tab.pallets.production_datetime",
	},
	{
		source: "expiration_date",
		rowSource: "expiration_datetime",
		label: "tab.pallets.expiration_datetime",
	},
	{ source: "sscc", label: "tab.pallets.sscc" },
];

const useStyles = makeStyles((theme) => ({
	header: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	headerBlock: { minWidth: 180 },
	headerLabel: { color: theme.palette.text.secondary },
	mismatch: { color: theme.palette.warning.dark, fontWeight: 600 },
	weightUnitInput: { maxWidth: 120 },
}));

const TotalsComparison = ({ printed, computed, label }) => {
	const classes = useStyles();
	const translate = useTranslate();
	const isMismatch = printed !== null && printed !== undefined && printed !== computed;

	return (
		<Typography variant="body2">
			{translate(label)}:{" "}
			<span className={isMismatch ? classes.mismatch : undefined}>
				{printed === null || printed === undefined
					? translate("osfc.dialogs.generate-pallets-from-pdf.not_printed")
					: printed}
			</span>
			{" / "}
			<span className={isMismatch ? classes.mismatch : undefined}>{computed}</span>
		</Typography>
	);
};

export const ExtractionSummaryHeader = ({ extraction, rows, disabled, onWeightUnitChange }) => {
	const classes = useStyles();
	const translate = useTranslate();

	const computedTotals = useMemo(
		() => ({
			total_pallets: rows.length,
			total_units: sumNumericBy(rows, "quantity"),
			total_net_weight: roundToTwo(sumNumericBy(rows, "net_weight")),
		}),
		[rows],
	);

	const printedTotals = extraction?.document_totals || {};
	const columnMapping = extraction?.column_mapping || {};

	const hasRowValue = (field) =>
		rows.some((row) => {
			const value = row[field.rowSource || field.source];
			return value !== null && value !== undefined && value !== "";
		});

	// A field with data but no column named for it is the reading an operator has no way to
	// check, so it says so rather than claiming the document printed no such column.
	const withColumn = (field) => ({
		...field,
		column:
			columnMapping[field.source] ||
			translate(
				hasRowValue(field)
					? "osfc.dialogs.generate-pallets-from-pdf.column_not_reported"
					: "osfc.dialogs.generate-pallets-from-pdf.not_printed",
			),
	});

	// An optional field earns a chip once it has data, even where the AI reported no column.
	const mappingFields = [
		...CORE_MAPPING_FIELDS.map(withColumn),
		...OPTIONAL_MAPPING_FIELDS.filter(
			(field) => columnMapping[field.source] || hasRowValue(field),
		).map(withColumn),
	];

	return (
		<div className={classes.header}>
			<div className={classes.headerBlock}>
				<Typography variant="caption" className={classes.headerLabel}>
					{translate("osfc.dialogs.generate-pallets-from-pdf.column_mapping")}
				</Typography>
				<div>
					{mappingFields.map((field) => (
						<Chip
							key={field.source}
							size="small"
							style={{ margin: 2 }}
							label={`${translate(field.label)}: ${field.column}`}
						/>
					))}
				</div>
			</div>

			<div className={classes.headerBlock}>
				<Typography variant="caption" className={classes.headerLabel}>
					{translate("osfc.dialogs.generate-pallets-from-pdf.weight_unit")}
				</Typography>
				<MuiTextField
					className={classes.weightUnitInput}
					value={extraction?.weight_unit || ""}
					placeholder={translate("osfc.dialogs.generate-pallets-from-pdf.not_printed")}
					onChange={(e) => onWeightUnitChange(e.target.value || null)}
					disabled={disabled}
				/>
			</div>

			<div className={classes.headerBlock}>
				<Typography variant="caption" className={classes.headerLabel}>
					{translate("osfc.dialogs.generate-pallets-from-pdf.printed_totals")}
					{" / "}
					{translate("osfc.dialogs.generate-pallets-from-pdf.computed_totals")}
				</Typography>
				<TotalsComparison
					label="tab.pallets.name"
					printed={printedTotals.total_pallets}
					computed={computedTotals.total_pallets}
				/>
				<TotalsComparison
					label="tab.pallets.quantity"
					printed={printedTotals.total_units}
					computed={computedTotals.total_units}
				/>
				<TotalsComparison
					label="tab.pallets.weight_net"
					printed={printedTotals.total_net_weight}
					computed={computedTotals.total_net_weight}
				/>
			</div>
		</div>
	);
};
